
import { Component, Vue, Prop, Watch, Mixins } from "vue-property-decorator";
import { namespace, Getter, State } from "vuex-class";
import { AuditService, MediaFile } from "@/services/auditService";
import { DeviceDetails, DeviceService } from "@sureview/v2-cameras";
import MatrixSidebarMixin from "@/components/media/sidebar/MatrixSidebarMixin";

import VuePerfectScrollbar from "vue-perfect-scrollbar";
import Sessions from "@/components/Sessions.vue";
import Tour from "@/components/media/sidebar/Tour.vue";
import CameraList from "@/components/media/sidebar/CameraList.vue";
import ClipsList from "@/components/media/sidebar/ClipsList.vue";
import AreaList from "@/components/media/sidebar/AreaList.vue";
import DeviceList from "@/components/media/sidebar/DeviceList.vue";
import StoredFileList from "@/components/media/sidebar/StoredFileList.vue";
import AudioControl from "@/components/audio/AudioControl.vue";

import { MatrixContents, MatrixCamera, MatrixStoredFile } from "@/store/site-monitor-cameras/types";
import { CameraType, ClipType } from "@/store/site-monitor-cameras/types";
import { EventDetails, EventRecord } from "@/store/site-monitor/types";
import { FeaturesList, UserPermissions, User } from "@/store/types";
import { get, isEmpty } from "lodash";

import { renderDistance } from "@/filters";

const Eventqueue = namespace("eventqueue");
const SiteMonitor = namespace("siteMonitor");
const SMCameras = namespace("siteMonitorCameras");
const Session = namespace("sessions");
const Devices = namespace("devices");
const Tours = namespace("tours");

@Component({
	components: {
		session: Sessions,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"clips-list": ClipsList,
		"camera-list": CameraList,
		"area-list": AreaList,
		"device-list": DeviceList,
		"stored-file-list": StoredFileList,
		tour: Tour,
		"audio-control": AudioControl
	},
	filters: {
		renderDistance
	}
})
export default class DeviceController extends Mixins(MatrixSidebarMixin) {
	@State("User") private user: User;

	@Getter("getFeaturesList") private featuresList: FeaturesList;
	@Getter("getPermissions") private permissions: UserPermissions;

	@SiteMonitor.Getter("getEventDetails") private getEventDetails: EventDetails;
	@SiteMonitor.Getter("getActiveMapItems") private mapItems: any;
	@SiteMonitor.Mutation private setActiveMapItemsRequired: any;

	@SiteMonitor.Getter("getAuditService") private auditService: any;
	@SiteMonitor.Getter("getSelectedEventRecord") private selectedEventRecord: EventRecord;

	@SMCameras.Getter("getDeviceControllerCameras") private cameras!: CameraType[];
	@SMCameras.Getter("getDeviceControllerClips") private clips!: ClipType[];
	@SMCameras.Getter("getDeviceControllerOutputs") private outputs!: any[];
	@SMCameras.Getter("getDeviceControllerStoredFiles") private files!: MatrixStoredFile[];
	@SMCameras.Getter("getAreaCameras") private groupCameras: MatrixCamera[];

	@SMCameras.Getter("getMediaMatrixLayers") private layers!: any[];
	@SMCameras.Getter("getMediaMatrixIsNew") private mediaMatrixIsNew: any;
	@SMCameras.Getter("getFetchingMatrix") private fetchingMediaMatrix: any;
	@SMCameras.Getter private getMatrixContents: any;
	@SMCameras.Getter private searchedCameras: any;
	@SMCameras.Getter private searchedOutputs: any;

	@SMCameras.Mutation private setDeviceControllerCameras: any;
	@SMCameras.Mutation private setDeviceControllerClips: any;
	@SMCameras.Mutation private setDeviceControllerOutputs: any;
	@SMCameras.Mutation private setPushContents: any;
	@SMCameras.Mutation private setMediaMatrixIsNew: any;

	@SMCameras.Action private fetchDevicesByGroup: any;

	@Tours.Getter private tourInProgress: boolean;

	@Prop({ default: "", type: String }) private tourName;

	private setInitialRecordForEventId: number = 0;
	private initialEventRecordID: number | null = null;
	private populateNearbyCamerasModal: boolean = false;
	private mapItemsSet: boolean = false;
	private fillStartIndex: number | null = null;
	private clipsQueue: any[] = [];

	public open = {
		cameras: true,
		clips: true,
		audio: true,
		outputs: true,
		areas: true,
		tours: true,
		files: true
	};

	public get showStoredFiles() {
		return get(this.featuresList, ["Alarms", "MediaMatrix", "Media"]) && this.permissions["canViewAttachedMedia"];
	}

	public get showAreas() {
		return get(this.featuresList, ["Alarms", "MediaMatrix", "AreaTree"]);
	}

	public get showTours() {
		return get(this.featuresList, ["Alarms", "MediaMatrix", "VideoTours"]);
	}

	public get showOutputs() {
		return this.permissions["canViewMediaMatrixControls"];
	}

	public get allowSpotlightScaling() {
		return get(this.featuresList, ["Alarms", "SiteMonitor", "Map", "SpotlightScaling"]);
	}

	/**
	 * @summary computed property which checks if audio is enabled in the feature list
	 * @returns { boolean } if the feature is enabled.
	 */
	public get isAudioEnabled(): boolean {
		return get(this.featuresList, ["Integration", "Audio"], false);
	}

	public get totalCells() {
		return this.layouts[this.layoutIndex][0] * this.layouts[this.layoutIndex][1];
	}

	private get cameraCount() {
		let currentStreamedCameraCount = Object.values(this.allMatrixContents)
			.filter(m => m && m.camera)
			.map(m => m.camera)
			.filter(
				c =>
					!this.cameras.some(nearbyCam => nearbyCam.objectID == c.objectID) &&
					!this.groupCameras.some(groupCam => groupCam.objectID == c.objectID)
			).length;

		return currentStreamedCameraCount + this.cameras.length + this.groupCameras.length;
	}

	@Watch("eventId")
	public onEventChanged(eventId: number, oldEventId: number) {
		if (eventId == 0) {
			this.mapItemsSet = false;
		}
	}

	@Watch("tourInProgress")
	private minimizeCollapseMenu() {
		if (this.tourInProgress) {
			Object.keys(this.open).forEach(v => (this.open[v] = false));
			this.open.tours = true;
			this.open.cameras = true;
		} else {
			Object.keys(this.open).forEach(v => (this.open[v] = true));
		}
	}

	@Watch("cameras")
	public async loadNearbyCameraLayer(newData: any, oldData: any) {
		/** Check to see if any of the new or old data incoming is invalid for this type of operation. */
		if (!newData || newData.length <= 0 || !oldData || oldData.length <= 0 || this.eventId == 0) return;
		if (this.getEventDetails && !this.getEventDetails.useNearbyCameras) return;

		// Event Types 'EventTypeID'
		const alarmEvent = 1;
		const patrolEvent = 2;

		const dataChanged = this.checkCamerasComparison(newData, oldData);
		if (this.getEventDetails.eventTypeID == alarmEvent) {
			this.loadAlarmEvent(newData, oldData, dataChanged);
		} else if (this.getEventDetails.eventTypeID == patrolEvent) {
			await this.loadNearbyCameras();
		}
	}

	@Watch("mapItems")
	public onMapItemsChanged(value: any, oldValue: any) {
		if (value) {
			this.setDevices(value);
		} else {
			this.setDeviceControllerCameras([]);
			this.mapItemsSet = false;
		}
	}

	@Watch("fetchingMediaMatrix")
	public fetchingMediaMatrixChanged(value: boolean, oldValue: boolean) {
		if (!value) {
			while (this.clipsQueue.length > 0) {
				this.fillIfNew(this.clipsQueue.shift());
			}
		}
	}

	public created() {
		this.updateSession({ resourceId: 3, eventId: this.eventId });
	}

	public mounted() {
		this.setActiveMapItemsRequired(true);
		this.expandDefaultSections();
	}

	private checkCamerasComparison(newData: any, oldData: any): boolean {
		return newData.count == oldData.count && JSON.stringify(newData) !== JSON.stringify(oldData);
	}

	private async loadAlarmEvent(newData: any, oldData: any, dataChanged: boolean) {
		if (!dataChanged || this.tourInProgress || !this.allowSpotlightScaling || !this.getEventDetails) {
			return;
		}

		this.populateNearbyCamerasModal = true;
	}

	public async loadNearbyCameras() {
		for (let index = 0; index < this.totalCells; index++) {
			let newContents = {};
			if (index < this.cameras.length) {
				newContents = {
					camera: {
						objectID: this.cameras[index].objectID,
						title: this.cameras[index].title,
						index: index + 1
					}
				};
			}

			await this.setPushContents({
				index: index + 1,
				newContents: newContents
			});
		}
		this.populateNearbyCamerasModal = false;
	}

	public setDevices(devices: any[]) {
		this.mapItemsSet = true;

		let cameras = devices.filter(device => device.itemType === 1);

		let outputs = devices.filter(device => device.itemType === 3 || device.itemType === 5);

		this.setDeviceControllerCameras(cameras);
		this.setDeviceControllerOutputs(outputs);

		if (this.fillStartIndex != null) {
			this.fillCellsWithMapItems();
		}
	}

	public fillCellsWithMapItems() {
		if (this.fillStartIndex != null) {
			let index = this.fillStartIndex;
			this.fillStartIndex = null;

			let cellCount = this.layoutHorizontal * this.layoutVertical;

			let leftToFill = cellCount - index;

			this.cameras.some((camera: any) => {
				if (camera.index == null) {
					this.setPushContents({
						index: index,
						newContents: {
							camera: camera
						},
						eventId: this.eventId
					});
					index++;
				}

				return index > cellCount;
			});
		}
	}

	private getMatrixClipsSet(): Set<string> {
		const matrixClips = new Set<string>();
		let maxtrixContents = this.allMatrixContents;

		// iterate over current cells in the matrix
		for (var matrixIndex in maxtrixContents) {
			if (maxtrixContents[matrixIndex].clip !== undefined) {
				// get the cells clip
				let clip = maxtrixContents[matrixIndex].clip.clip;
				if (clip != null) {
					// add index value to the array under the clips fileId
					matrixClips.add(clip.FileIdentifier.toString());
				}
			}
		}

		return matrixClips;
	}

	private clipsRetrieved(clips: any) {
		if (this.fetchingMediaMatrix) {
			this.clipsQueue.push(clips);
		} else {
			this.fillIfNew(clips);
		}
	}

	private fillIfNew({ eventRecordID, cameras, newClips }) {
		let mediaMatrixIsNew = this.mediaMatrixIsNew;
		if (mediaMatrixIsNew) {
			console.log("Matrix: DeviceController-fillIfNew: Matrix is new!");

			this.setMediaMatrixIsNew(false);
			this.initialEventRecordID = eventRecordID;

			this.setInitialRecordForEventId = this.selectedEventRecord.eventID;

			let cellCount = this.layoutHorizontal * this.layoutVertical;
			let index = 1;

			// Set clips
			if (this.clips && this.clips.length > 0) {
				this.clips.some((clip: any) => {
					this.setPushContents({
						index: index,
						newContents: {
							clip: {
								eventRecordID: clip.EventRecordId,
								clip: clip
							}
						},
						eventId: this.eventId
					});
					index++;

					return index > cellCount;
				});
			}

			//Set event record linked cameras
			if (cameras && cameras.length > 0 && index <= cellCount) {
				cameras.some((camera: any) => {
					this.setPushContents({
						index: index,
						newContents: {
							camera: {
								objectID: camera.deviceID,
								title: camera.title
							}
						},
						eventId: this.eventId
					});
					index++;

					return index > cellCount;
				});
			}

			//If we have map items already, add them. If not, save the current index ready to be used when they are ready.
			if (index <= cellCount) {
				this.fillStartIndex = index;

				if (this.mapItemsSet) {
					let leftToFill = cellCount - index;
					this.fillCellsWithMapItems();
				}
			}
		} else if (newClips && newClips.length > 0) {
			const takenIndexes = new Set<number>();
			const matrixClips = this.getMatrixClipsSet();

			newClips.forEach(clip => {
				if (matrixClips.has(clip.FileIdentifier)) return;

				let emptyIndex = 1;
				let finalIndex = this.layoutHorizontal * this.layoutVertical;
				while (emptyIndex <= finalIndex) {
					let cellContents = this.getMatrixContents(emptyIndex);
					if (isEmpty(cellContents) && !takenIndexes.has(emptyIndex)) {
						break;
					} else {
						emptyIndex++;
					}
				}

				if (emptyIndex > finalIndex) {
					finalIndex = this.layoutHorizontal * this.layoutVertical;
					while (finalIndex > 0) {
						let cellContents = this.getMatrixContents(finalIndex);
						if (cellContents?.clip == null && !takenIndexes.has(finalIndex)) {
							break;
						} else {
							finalIndex--;
						}
					}
				} else {
					finalIndex = emptyIndex;
				}

				if (finalIndex > 0) {
					takenIndexes.add(finalIndex);
					this.setPushContents({
						index: finalIndex,
						newContents: {
							clip: {
								eventRecordID: clip.EventRecordId,
								clip: clip
							}
						},
						eventId: this.eventId
					});
				}
			});
		}
	}

	private setTourName(data: any) {
		this.tourName = data;
	}

	/**
	 * Uses the mediaMatrixConfig passed as part of the user login result to set the default
	 * expanded or collapsed state of each sidebar section.
	 */
	private expandDefaultSections() {
		if (!this.user.mediaMatrixConfig) {
			return;
		}

		let {
			videoToursExpanded,
			clipsExpanded,
			camerasExpanded,
			controlsExpanded,
			areasExpanded,
			filesExpanded,
			audioExpanded
		} = this.user.mediaMatrixConfig;

		this.open = {
			tours: videoToursExpanded,
			clips: clipsExpanded,
			cameras: camerasExpanded,
			outputs: controlsExpanded,
			areas: areasExpanded,
			files: filesExpanded,
			audio: audioExpanded
		};
	}
}
